// @flow
import {normalize} from 'normalizr'
import * as Payload from './types/payload'
import {actionTypes} from './constants'
import {accountManagersSchema, companiesSchema} from './schema'
import {parseAttributes} from './utils'
import type {AdminState} from './types/index'
import type {Handlers, Action} from 'modules/Common/types'

export const initialState: AdminState = {
  accountManagers: {
    records: {},
    pageNumber: 0,
    numberOfPages: 1,
    pageSize: 20,
    ids: []
  },
  roleList: [],
  clients: {
    records: {},
    pageNumber: 0,
    numberOfPages: 1,
    pageSize: 20,
    ids: []
  },
  accountManagerDetail: null,
  isLoadingAccountManagers: false,
  isLoadingClients: false,
  companies: {
    records: {},
    pageNumber: 0,
    numberOfPages: 1,
    pageSize: 20,
    ids: []
  },
  companiesSuggestion: [],
  accountManagersSuggestion: [],
  isLoadingCompaniesSuggestion: false,
  isLoadingAccountManagersSuggestion: false,
  isLoadingCompanies: false,
  error: null,
  isInvitingAM: false,
  isInvitingClient: false,
  companyProfile: {}
}

const handlers: Handlers<AdminState> = {
  [actionTypes.GET_ACCOUNT_MANAGERS_REQUEST]: (
    state,
    action: Action<Payload.GetAccountManagersRequest>
  ) => {
    return {
      ...state,
      isLoadingAccountManagers: true
    }
  },
  [actionTypes.GET_ACCOUNT_MANAGERS_SUCCESS]: (
    state,
    action: Action<Payload.GetAccountManagersSuccess>
  ) => {
    const result =
      action.payload.records && action.payload.records.length > 0
        ? normalize(
            {accountManagers: action.payload.records},
          {
            accountManagers: [accountManagersSchema]
          }
          )
        : {entities: {records: {}}}
    return {
      ...state,
      isLoadingAccountManagers: false,
      accountManagers: {
        ...state.accountManagers,
        records: {...state.accountManagers.records, ...result.entities.records},
        ids: Object.keys(result.entities.records),
        pageNumber: action.payload.pageNumber,
        numberOfPages: action.payload.numberOfPages
      }
    }
  },
  [actionTypes.GET_ACCOUNT_MANAGERS_FAILURE]: (
    state,
    action: Action<Payload.GetAccountManagersFailure>
  ) => {
    return {
      ...state,
      accountManagers: {
        ...state.accountManagers,
        ids: []
      },
      isLoadingAccountManagers: false
    }
  },

  [actionTypes.GET_ROLES_SUCCESS]: (
    state,
    action: Action<Payload.GetRolesSuccess>
  ) => {
    return {
      ...state,
      roleList: action.payload ? action.payload.records || [] : []
    }
  },

  [actionTypes.GET_COMPANIES_REQUEST]: (
    state,
    action: Action<Payload.GetCompaniesRequest>
  ) => {
    return {
      ...state,
      isLoadingCompanies: true
    }
  },
  [actionTypes.GET_COMPANIES_SUCCESS]: (
    state,
    action: Action<Payload.GetCompaniesSuccess>
  ) => {
    const result =
      action.payload.records && action.payload.records.length > 0
        ? normalize(
            {companies: action.payload.records},
          {
            companies: [companiesSchema]
          }
          )
        : {entities: {records: {}}}
    return {
      ...state,
      isLoadingCompanies: false,
      companies: {
        ...state.companies,
        records: {...state.companies.records, ...result.entities.records},
        ids: Object.keys(result.entities.records),
        pageNumber: action.payload.pageNumber,
        numberOfPages: action.payload.numberOfPages
      }
    }
  },
  [actionTypes.GET_COMPANIES_FAILURE]: (
    state,
    action: Action<Payload.GetCompaniesFailure>
  ) => {
    return {
      ...state,
      companies: {
        ...state.companies,
        ids: []
      },
      isLoadingCompanies: false
    }
  },

  [actionTypes.GET_COMPANIES_SUGGESTION_REQUEST]: (
    state,
    action: Action<Payload.GetCompaniesRequest>
  ) => {
    return {
      ...state,
      isLoadingCompaniesSuggestion: true
    }
  },
  [actionTypes.GET_COMPANIES_SUGGESTION_SUCCESS]: (
    state,
    action: Action<Payload.GetCompaniesSuccess>
  ) => {
    return {
      ...state,
      isLoadingCompaniesSuggestion: false,
      companiesSuggestion: action.payload.records
    }
  },
  [actionTypes.GET_COMPANIES_SUGGESTION_FAILURE]: (
    state,
    action: Action<Payload.GetCompaniesFailure>
  ) => {
    return {
      ...state,
      companiesSuggestion: {},
      isLoadingCompaniesSuggestion: false
    }
  },

  [actionTypes.ADD_OR_UPDATE_AM_REQUEST]: (
    state,
    action: Action<Payload.AddOrUpdateAMRequest>
  ) => {
    return {
      ...state,
      error: null,
      isInvitingAM: true
    }
  },
  [actionTypes.ADD_OR_UPDATE_AM_SUCCESS]: (
    state,
    action: Action<Payload.AddOrUpdateAMSuccess>
  ) => {
    let AMListNew = {...state.accountManagers.records}
    AMListNew[action.payload.record.id] = action.payload.record
    let AMIds = [...state.accountManagers.ids]
    AMIds.push(action.payload.record.id)

    return {
      ...state,
      error: null,
      isInvitingAM: false,
      accountManagers: {
        ...state.accountManagers,
        records: AMListNew,
        ids: AMIds
      }
    }
  },
  [actionTypes.ADD_OR_UPDATE_AM_FAILURE]: (
    state,
    action: Action<Payload.AddOrUpdateCompanyFailure>
  ) => {
    return {
      ...state,
      isInvitingCompany: false,
      error: action.payload.error
    }
  },

  [actionTypes.ADD_OR_UPDATE_COMPANY_REQUEST]: (
    state,
    action: Action<Payload.AddOrUpdateCompanyRequest>
  ) => {
    return {
      ...state,
      error: null
    }
  },
  [actionTypes.ADD_OR_UPDATE_COMPANY_SUCCESS]: (
    state,
    action: Action<Payload.AddOrUpdateCompanySuccess>
  ) => {
    let CompanyListNew = {...state.companies.records}
    CompanyListNew[action.payload.record.id] = action.payload.record
    let CompanyIds = [...state.companies.ids]
    CompanyIds.push(action.payload.record.id)

    return {
      ...state,
      error: null,
      companies: {
        ...state.companies,
        records: CompanyListNew,
        ids: CompanyIds
      }
    }
  },
  [actionTypes.ADD_OR_UPDATE_COMPANY_FAILURE]: (
    state,
    action: Action<Payload.AddOrUpdateCompanyFailure>
  ) => {
    return {
      ...state,
      error: action.payload.error
    }
  },

  [actionTypes.GET_CLIENTS_REQUEST]: (
    state,
    action: Action<Payload.GetClientsRequest>
  ) => {
    return {
      ...state,
      isLoadingClients: true
    }
  },
  [actionTypes.GET_CLIENTS_SUCCESS]: (
    state,
    action: Action<Payload.GetClientsSuccess>
  ) => {
    const result =
      action.payload.records && action.payload.records.length > 0
        ? normalize(
            {clients: action.payload.records},
          {
            clients: [accountManagersSchema]
          }
          )
        : {entities: {records: {}}}
    return {
      ...state,
      isLoadingClients: false,
      clients: {
        ...state.clients,
        records: {...state.clients.records, ...result.entities.records},
        ids: Object.keys(result.entities.records),
        pageNumber: action.payload.pageNumber,
        numberOfPages: action.payload.numberOfPages
      }
    }
  },
  [actionTypes.GET_CLIENTS_FAILURE]: (
    state,
    action: Action<Payload.GetClientsFailure>
  ) => {
    return {
      ...state,
      clients: {
        ...state.clients,
        ids: []
      },
      isLoadingClients: false
    }
  },

  [actionTypes.INVITE_NEW_CLIENT_REQUEST]: (
    state,
    action: Action<Payload.InviteNewClientRequest>
  ) => {
    return {
      ...state,
      isInvitingClient: true
    }
  },
  [actionTypes.INVITE_NEW_CLIENT_SUCCESS]: (
    state,
    action: Action<Payload.InviteNewClientSuccess>
  ) => {
    let ClientListNew = {...state.clients.records}
    ClientListNew[action.payload.record.id] = action.payload.record
    let ClientIds = [...state.clients.ids]
    ClientIds.push(action.payload.record.id)

    return {
      ...state,
      isInvitingClient: false,
      clients: {
        ...state.clients,
        records: ClientListNew,
        ids: ClientIds
      }
    }
  },
  [actionTypes.INVITE_NEW_CLIENT_FAILURE]: (
    state,
    action: Action<Payload.InviteNewClientFailure>
  ) => {
    return {
      ...state,
      isInvitingClient: false,
      error: action.payload.error
    }
  },

  [actionTypes.GET_ACCOUNT_MANAGERS_SUGGESTION_REQUEST]: (
    state,
    action: Action<Payload.GetAccountManagersRequest>
  ) => {
    return {
      ...state,
      isLoadingAccountManagersSuggestion: true
    }
  },
  [actionTypes.GET_ACCOUNT_MANAGERS_SUGGESTION_SUCCESS]: (
    state,
    action: Action<Payload.GetAccountManagersSuccess>
  ) => {
    return {
      ...state,
      isLoadingAccountManagersSuggestion: false,
      accountManagersSuggestion: action.payload.records
    }
  },
  [actionTypes.GET_ACCOUNT_MANAGERS_SUGGESTION_FAILURE]: (
    state,
    action: Action<Payload.GetAccountManagersFailure>
  ) => {
    return {
      ...state,
      accountManagersSuggestion: {},
      isLoadingAccountManagersSuggestion: false
    }
  },
  [actionTypes.GET_COMPANY_PROFILE_REQUEST]: state => {
    return {
      ...state,
      companyProfile: {}
    }
  },
  [actionTypes.GET_COMPANY_PROFILE_SUCCESS]: (state, action) => {
    return {
      ...state,
      companyProfile: parseAttributes(action.payload.profile.attributes)
    }
  }
}

export default function reducer(
  state: AdminState = initialState,
  action: Action<*>
) {
  if (!action || !action.type) {
    return state
  }

  const handler = handlers[action.type]

  if (handler == null) {
    return state
  }

  return handler(state, action)
}
